/**
 * Dates on this site are written in ONE language, and it is not the reader's operating system's.
 *
 * `toLocaleDateString(undefined, …)` reads the browser locale, so an English page rendered a Russian
 * "9 сент." beside an English "NEXT DEPARTURE" heading for anyone whose machine is set to Russian —
 * reported from the carrier comparison, 2026-09-09. Every other string on the page is authored English;
 * a date that follows the machine instead is the one element that changes under the reader, and it reads
 * as a bug rather than as a courtesy.
 *
 * The month names are OURS rather than the runtime's, which is the second half of the same lesson.
 * Naming a locale fixes the language but not the spelling: `en-GB` renders September as "Sep" under one
 * ICU build and "Sept" under another (Bun and Node disagree on this very machine), so the column would
 * change width and wording depending on the browser, and a test pinning either spelling fails on the
 * other runtime. A table of twelve strings has no such dependency.
 *
 * Day-first, because that is the order the rest of the product already uses (the workspace schedule
 * table has always been explicit about it) and the order most of our customers outside North America
 * write in.
 *
 * Use these instead of calling toLocaleDateString directly. A locale-less call was the whole defect, and
 * the only way it stays fixed is that there is one place to fix.
 */
const SITE_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

/**
 * The STORED digits, never the reader's zone (SCHED-9): a sailing time is the port's wall clock written
 * as `…Z`, so its date is the UTC date of that string. Read in the reader's zone, a date-only sailing
 * (`2026-09-29T00:00:00Z`) showed 28 Sep to everyone west of Greenwich.
 */
/** `YYYY-MM-DDTHH:MM[:SS[.fff]]` with nothing after it: the API's port-local form. */
const NO_OFFSET = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/

function parts(
  value: string | number | Date | null | undefined
): { day: number; month: string } | null {
  if (value == null) return null
  // A port-local time arrives without an offset (SCHED-107); read its digits as-is rather than
  // through the browser's zone, or an eastern browser shows the day before.
  const d = new Date(typeof value === 'string' && NO_OFFSET.test(value) ? `${value}Z` : value)
  // toLocaleDateString itself never throws (it renders "Invalid Date"), but a caller that pre-converts
  // via toISOString() can throw RangeError on a bad timestamp, so the guard stays here where it is shared.
  if (Number.isNaN(d.getTime())) return null
  const month = SITE_MONTHS[d.getUTCMonth()]
  return month ? { day: d.getUTCDate(), month } : null
}

/** "9 Sep" — the compact form used in tables, chips and timelines. */
export function shortDate(value: string | number | Date | null | undefined): string {
  const p = parts(value)
  return p ? `${p.day} ${p.month}` : '—'
}

/** "09 Sep" — the zero-padded form, where a column of dates should line up. */
export function shortDatePadded(value: string | number | Date | null | undefined): string {
  const p = parts(value)
  return p ? `${String(p.day).padStart(2, '0')} ${p.month}` : '—'
}
